import * as React from "react";

import { Box, Button, Tooltip } from "@costar/theme-ui";
import { IconGlobe } from "@costar/theme-ui-icons/csg/IconGlobe";
import type { PopoverSelectContainerProps } from "@costar/theme-ui-select";

import { useOutsideAlerter, useStrictI18N } from "../../hooks";
import PreferenceSelectorContent from "../../shared/preference-selector/preferences-selector-content";
import { useAuthNavConfigContext } from "../nav-config";

import { getSelectedPreferences } from "$client/utils/selected-preferences";
import { AuthenticatedClientConfig, useConfigContext } from "$common/config";
import { client } from "$graphql/client/client-side-provider";
import { CultureOption, CurrencyOption, Maybe, SupportedPreferences } from "$graphql/core";
import { SUPPORTED_PREFERENCES_QUERY } from "$graphql/core/queries/supported-preferences";

type PreferencesInfo = {
    supportedPreferences: SupportedPreferences | null;
    isBenchmarkingSubscriber?: boolean;
};

const PreferenceSelectorDropdown: React.FC<PopoverSelectContainerProps> = props => {
    const [open, setOpen] = React.useState<boolean>(false);

    const handleClose = React.useCallback(() => {
        setOpen(false);
    }, []);

    const config = useConfigContext() as AuthenticatedClientConfig;
    const { userPreferences, options } = config;

    const { navConfig } = useAuthNavConfigContext();

    /** When clicked on a button, toggle the dropdown*/
    const handleButtonClick = React.useCallback(() => {
        setOpen(!open);
    }, [open]);

    const dropdownRef = React.useRef(null);
    const tooltipRef = React.useRef(null);
    useOutsideAlerter([dropdownRef, tooltipRef], handleClose);

    const [preferencesData, setPreferencesData] = React.useState<PreferencesInfo>({
        supportedPreferences: null,
        isBenchmarkingSubscriber: false,
    });

    const [selectedLocaleState, setSelectedLocaleState] = React.useState<Maybe<CultureOption> | null>(null);

    const [selectedCurrencyState, setSelectedCurrencyState] = React.useState<Maybe<CurrencyOption> | null>(null);

    const goBackUrl = typeof window !== "undefined" ? window.location.href || "" : "";
    const centralPrefUrl = `${options?.navLinks?.preferences}?goBackUrl=${goBackUrl}`;

    React.useEffect(() => {
        client.query({ query: SUPPORTED_PREFERENCES_QUERY }).then(
            (res: {
                data: {
                    user: {
                        supportedPreferences: SupportedPreferences;
                    };
                };
            }) => {
                const supportedPreferences = res?.data?.user?.supportedPreferences;

                const isBenchmarkingSubscriber = navConfig.find(item => item.id === "STR")?.isUserSubscriber;

                if (supportedPreferences) {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    const { selectedLocale, selectedCurrency } = getSelectedPreferences(
                        supportedPreferences,
                        userPreferences,
                        isBenchmarkingSubscriber ?? false
                    );

                    setPreferencesData({
                        supportedPreferences,
                        isBenchmarkingSubscriber: isBenchmarkingSubscriber ?? false,
                    });

                    setSelectedLocaleState(selectedLocale ?? null);
                    setSelectedCurrencyState(selectedCurrency ?? null);
                }
            }
        );
    }, [navConfig, userPreferences, userPreferences.CultureName, userPreferences.CurrencyName]);

    return (
        <Tooltip
            interactive
            arrow
            width="100%"
            maxWidth="none"
            placement="bottom-end"
            top="6px"
            open={open}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            onClose={handleClose}
            ref={tooltipRef}
            title={
                <Box ref={dropdownRef} style={{ padding: "0", width: "100%" }}>
                    <PreferenceSelectorContent
                        languageOptions={preferencesData.supportedPreferences?.cultures}
                        currencyOptions={preferencesData.supportedPreferences?.currencies}
                        benchmarkCultures={preferencesData.supportedPreferences?.benchmarkCultures}
                        selectedLocale={selectedLocaleState}
                        setSelectedLocaleState={setSelectedLocaleState}
                        selectedCurrency={selectedCurrencyState}
                        setSelectedCurrencyState={setSelectedCurrencyState}
                        isBenchmarkingSubscriber={preferencesData.isBenchmarkingSubscriber}
                        centralPrefUrl={centralPrefUrl}
                    />
                </Box>
            }
        >
            <Button
                automation-id="uui-language-selector-dropdown"
                style={{ border: "none", padding: 0 }}
                onClick={handleButtonClick}
                icon={<IconGlobe size="24px" color="neutrals.10" />}
            />
        </Tooltip>
    );
};

export default PreferenceSelectorDropdown;
